import { z } from 'zod';

import {
  createEntityAdapterSchema,
  EntityBaseSchema,
  EntityMetadataSchema,
  IdSchema,
  NonEmptyStringSchema,
  ReferenceSchema,
  TrimmedStringSchema,
} from '../../base';
import { FunctionSchema } from '../functions';

// Flow-specific enums
export const ConditionType = {
  ExitFlowCondition: 'exitFlowCondition' as const,
  StepCondition: 'stepCondition' as const,
  NoCodeStepCondition: 'noCodeStepCondition' as const,
  FunctionStepCondition: 'functionStepCondition' as const,
};

export const ConditionTypeSchema = z.nativeEnum(ConditionType);

export enum IngressPosition {
  Left = 'left',
  Right = 'right',
  Bottom = 'bottom',
  Top = 'top',
}

export const IngressPositionSchema = z.nativeEnum(IngressPosition);

// Position schema
export const StepPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export type StepPosition = z.infer<typeof StepPositionSchema>;

// ASR biasing schema
export const AsrKeywordsSchema = z.array(z.string());

export const StepAsrBiasingSchema = z.object({
  alphanumeric: z.boolean().default(false),
  nameSpelling: z.boolean().default(false),
  numeric: z.boolean().default(false),
  partySize: z.boolean().default(false),
  preciseDate: z.boolean().default(false),
  relativeDate: z.boolean().default(false),
  singleNumber: z.boolean().default(false),
  time: z.boolean().default(false),
  yesNo: z.boolean().default(false),
  address: z.boolean().default(false),
  customKeywords: AsrKeywordsSchema.default([]),
  isEnabled: z.boolean().default(false),
});

export type StepAsrBiasing = z.infer<typeof StepAsrBiasingSchema>;

// DTMF config schema
export const StepDTMFConfigSchema = z.object({
  isEnabled: z.boolean().default(false),
  interDigitTimeout: z.number().int().min(0).default(0),
  maxDigits: z.number().int().min(0).default(0),
  endKey: z.string().default('#'),
  collectWhileAgentSpeaking: z.boolean().default(false),
  isPii: z.boolean().default(false),
});

export type StepDTMFConfig = z.infer<typeof StepDTMFConfigSchema>;

// Step references schema
export const StepReferencesSchema = z.object({
  sms: ReferenceSchema,
  handoff: ReferenceSchema,
  attributes: ReferenceSchema,
  transitionFunctions: ReferenceSchema,
  globalFunctions: ReferenceSchema,
  entities: ReferenceSchema,
  variables: ReferenceSchema,
});

export type AdvancedStepReferences = z.infer<typeof StepReferencesSchema>;

// No-code step references schema
export const NoCodeStepReferencesSchema = z.object({
  entities: ReferenceSchema,
  attributes: ReferenceSchema,
  variables: ReferenceSchema,
  extractedEntities: ReferenceSchema,
});

export type DefaultStepReferences = z.infer<typeof NoCodeStepReferencesSchema>;

export const StepNameSchema = NonEmptyStringSchema.regex(
  /^[a-zA-Z0-9\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF_ &,/.-]+$/,
);

export const StepPromptSchema = TrimmedStringSchema;

// Flow step schema
export const BaseFlowStepSchema = z.object({
  name: StepNameSchema,
  prompt: StepPromptSchema,
  references: StepReferencesSchema,
});

export const FlowStepSchema = EntityBaseSchema.merge(BaseFlowStepSchema).extend(
  {
    position: StepPositionSchema,
    asrBiasing: StepAsrBiasingSchema,
    dtmfConfig: StepDTMFConfigSchema,
  },
);

export const AdvancedStepSchema = EntityBaseSchema.merge(FlowStepSchema)
  .extend({
    type: z.literal('advanced_step'),
  })
  .merge(EntityMetadataSchema);

export type AdvancedStep = z.infer<typeof AdvancedStepSchema>;

// Condition schemas
export const ConditionDetailsSchema = z.object({
  label: z.string().trim().min(1).max(50),
  description: z.string().optional(),
  position: StepPositionSchema,
  requiredEntities: z.array(IdSchema).default([]),
  ingressPosition: IngressPositionSchema.describe(
    "Direction connector enters from. USE 'top' or 'bottom' ONLY - left/right are not supported by the UI.",
  ),
});

export type DefaultConditionDetails = z.infer<typeof ConditionDetailsSchema>;

export const ExitFlowConditionSchema = z
  .object({
    $case: z.literal(ConditionType.ExitFlowCondition),
    value: z.object({
      details: ConditionDetailsSchema,
      exitFlowPosition: StepPositionSchema,
    }),
  })
  .describe(
    'Exit the flow entirely. Use when the conversation should end or hand off.',
  );

export type ExitFlowCondition = z.infer<typeof ExitFlowConditionSchema>;

export const StepConditionSchema = z
  .object({
    $case: z.literal(ConditionType.StepCondition),
    value: z.object({
      details: ConditionDetailsSchema,
      childStepId: IdSchema,
    }),
  })
  .describe(
    'Connect to a legacy/advanced step. Rarely used - prefer noCodeStepCondition for visual flow builder steps.',
  );

export type AdvancedStepCondition = z.infer<typeof StepConditionSchema>;

export const NoCodeStepConditionSchema = z
  .object({
    $case: z.literal(ConditionType.NoCodeStepCondition),
    value: z.object({
      details: ConditionDetailsSchema,
      childStepId: IdSchema,
    }),
  })
  .describe(
    'Connect to another no-code step. USE THIS for connecting steps in the visual flow builder.',
  );

export type DefaultStepCondition = z.infer<typeof NoCodeStepConditionSchema>;

export const FunctionStepConditionSchema = z
  .object({
    $case: z.literal(ConditionType.FunctionStepCondition),
    value: z.object({
      details: ConditionDetailsSchema,
      childStepId: IdSchema,
    }),
  })
  .describe(
    'Connect to a function step. Use when the target step is a function/code step.',
  );

export type FunctionStepCondition = z.infer<typeof FunctionStepConditionSchema>;

export const ConditionUnionSchema = z.discriminatedUnion('$case', [
  ExitFlowConditionSchema,
  StepConditionSchema,
  NoCodeStepConditionSchema,
  FunctionStepConditionSchema,
]);

export type ConditionConfig = z.infer<typeof ConditionUnionSchema>;

export const BaseConditionSchema = z.object({
  config: ConditionUnionSchema,
});

export const ConditionSchema = BaseConditionSchema.extend({
  id: IdSchema,
});

export type Condition = z.infer<typeof ConditionSchema>;

// No-code step schema
export const BaseNoCodeStepSchema = z.object({
  name: StepNameSchema.describe(
    "Step name in snake_case (e.g., 'ask_party_size', 'confirm_booking', 'check_availability')",
  ),
  prompt: StepPromptSchema.describe(
    "The message the bot will say or ask. For 'say' steps: this is what the bot says. For 'ask' steps: this is the question the bot asks the user.",
  ),
  position: StepPositionSchema.describe(
    'X/Y coordinates for visual positioning in the flow canvas (e.g., {x: 100, y: 200}). Used for UI layout.',
  ),
  references: NoCodeStepReferencesSchema.describe(
    'References to other Studio entities this step uses (variables, entities, functions, handoff config, etc.)',
  ),
  conditions: z
    .array(ConditionSchema)
    .describe(
      "Outgoing connections from this step to other steps. Each condition defines: 1) which step to go to next (toStepId), 2) when to take that path (label like 'yes', 'no', 'found'), 3) optional condition logic. Every step except 'end' type must have at least one condition. Use meaningful labels that describe the transition.",
    ),
});

export const NoCodeStepSchema = EntityBaseSchema.merge(BaseNoCodeStepSchema);

export const DefaultStepSchema = NoCodeStepSchema.extend({
  type: z.literal('default_step'),
}).merge(EntityMetadataSchema);

export type DefaultStep = z.infer<typeof DefaultStepSchema>;

export const TransitionFunctionReferencesSchema = z.object({
  flowSteps: ReferenceSchema,
  variables: ReferenceSchema,
});

export const FlowFunctionSchema = FunctionSchema.omit({
  references: true,
})
  .extend({
    references: TransitionFunctionReferencesSchema,
  })
  .merge(EntityMetadataSchema);

export type FlowFunction = z.infer<typeof FlowFunctionSchema>;

// Function step schema
export const FunctionStepSchema = EntityBaseSchema.extend({
  name: StepNameSchema,
  position: StepPositionSchema,
  function: FunctionSchema.omit({
    references: true,
    description: true,
    parameters: true,
  }),
})
  .extend({
    type: z.literal('function_step'),
    conditions: z.array(ConditionSchema),
  })
  .merge(EntityMetadataSchema);

export type FunctionStep = z.infer<typeof FunctionStepSchema>;

export const StepSchema = z.discriminatedUnion('type', [
  DefaultStepSchema,
  AdvancedStepSchema,
  FunctionStepSchema,
]);

export type Step = z.infer<typeof StepSchema>;

// Flow schema
export const BaseFlowSchema = z.object({
  name: NonEmptyStringSchema.describe(
    "Flow name in snake_case (e.g., 'table_reservation', 'check_availability'). This is the internal identifier for the flow.",
  ),
  description: TrimmedStringSchema.describe(
    "Human-readable description of what this flow does (e.g., 'Handles table reservation requests from customers')",
  ),
  startStepId: IdSchema.describe(
    'ID of the first step in the flow. Must reference an existing step in the noCodeSteps array.',
  ),
});

export const FlowSchema = EntityBaseSchema.merge(BaseFlowSchema)
  .extend({
    steps: createEntityAdapterSchema(StepSchema),
    transitionFunctions: createEntityAdapterSchema(FlowFunctionSchema),
  })
  .merge(EntityMetadataSchema);

export type Flow = z.infer<typeof FlowSchema>;

export const FlowIdSchema = z.object({
  flowId: IdSchema,
});

export const StepIdSchema = FlowIdSchema.extend({
  stepId: IdSchema,
});

export const ConditionIdSchema = StepIdSchema.extend({
  conditionId: IdSchema,
});
